import {
  isScopeFilterKey,
  SCOPE_ATTENTION_FILTER_TYPES,
  type ScopeFilterKey,
  toCanonicalScopeFilterKey,
} from '@/Client/Lib/scopeAttentionFilterLib';
import { isOverallTaskStatus } from '@/Client/Lib/scopeTaskStatusLib';
import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';

export const SCOPE_ATTENTION_FILTERS_PARAM = 'scopeFilters';
export const SCOPE_ATTENTION_TASK_STATUS_PARAM = 'scopeTaskStatus';

const parseFilterTokens = (rawValue: string | null): ScopeFilterKey[] => {
  if (!rawValue?.trim()) return [];
  const filters: ScopeFilterKey[] = [];
  for (const token of rawValue.split(',')) {
    const trimmed = token.trim();
    if (!trimmed || !isScopeFilterKey(trimmed)) continue;
    filters.push(toCanonicalScopeFilterKey(trimmed));
    break;
  }
  return filters;
};

export const readScopeAttentionFiltersFromSearchParams = (
  searchParams: URLSearchParams,
): ReadonlySet<ScopeFilterKey> => new Set(parseFilterTokens(searchParams.get(SCOPE_ATTENTION_FILTERS_PARAM)));

export const writeScopeAttentionFiltersToSearchParams = (
  searchParams: URLSearchParams,
  activeFilters: ReadonlySet<ScopeFilterKey>,
) => {
  const next = new URLSearchParams(searchParams);
  // Catalog order still decides among the fixed filters, so writing stays deterministic; an
  // org's own modifier keys have no catalog, so one of those is taken as it comes.
  const selectedFilter = SCOPE_ATTENTION_FILTER_TYPES.find(filter => activeFilters.has(filter))
    ?? [...activeFilters][0];
  const orderedFilters = selectedFilter ? [selectedFilter] : [];
  if (orderedFilters.length === 0) {
    next.delete(SCOPE_ATTENTION_FILTERS_PARAM);
  } else {
    next.set(SCOPE_ATTENTION_FILTERS_PARAM, orderedFilters.join(','));
  }
  return next;
};

export const toggleScopeAttentionFilterInSearchParams = (
  searchParams: URLSearchParams,
  filter: ScopeFilterKey,
) => {
  // Canonicalized on both sides, so a legacy key toggling against a stored canonical one (or the
  // reverse) still recognises itself as the same filter.
  const toggled = toCanonicalScopeFilterKey(filter);
  const activeFilters = new Set(readScopeAttentionFiltersFromSearchParams(searchParams));
  const next = new URLSearchParams(searchParams);
  next.delete(SCOPE_ATTENTION_TASK_STATUS_PARAM);
  return writeScopeAttentionFiltersToSearchParams(next, activeFilters.has(toggled) ? new Set() : new Set([toggled]));
};

export const readScopeAttentionTaskStatusFromSearchParams = (
  searchParams: URLSearchParams,
): OverallTaskStatus | undefined => {
  const rawValue = searchParams.get(SCOPE_ATTENTION_TASK_STATUS_PARAM)?.trim();
  return rawValue && isOverallTaskStatus(rawValue) ? rawValue : undefined;
};

export const toggleScopeAttentionTaskStatusInSearchParams = (
  searchParams: URLSearchParams,
  status: OverallTaskStatus,
) => {
  const next = new URLSearchParams(searchParams);
  const activeStatus = readScopeAttentionTaskStatusFromSearchParams(searchParams);
  next.delete(SCOPE_ATTENTION_FILTERS_PARAM);
  if (activeStatus === status) next.delete(SCOPE_ATTENTION_TASK_STATUS_PARAM);
  else next.set(SCOPE_ATTENTION_TASK_STATUS_PARAM, status);
  return next;
};

export const clearScopeAttentionSelectionFromSearchParams = (searchParams: URLSearchParams) => {
  const next = new URLSearchParams(searchParams);
  next.delete(SCOPE_ATTENTION_FILTERS_PARAM);
  next.delete(SCOPE_ATTENTION_TASK_STATUS_PARAM);
  return next;
};
